import { Box, Card, CardContent, Chip, Divider, Grid, IconButton, Typography } from '@mui/material'
import { useNotification } from '@/hooks/useNotification'
import { useTranslator } from '@/hooks/useTranslator'
import { FormProvider, useForm } from 'react-hook-form'
import { Button } from '@/@core/components/mui/Button'
import { BoxPage } from '@/components/common'
import { FormInput } from '@/components/form/FormInput'
import {
  getStatusConfig,
  IBalanceTransactionRecord,
  useBalanceTransactionUpdateMutation,
  useTransactionStatusOptions
} from '@/@data/balance-transaction'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { CompanyStatusEnum, getBalanceTransactionEditSchema, ICompany, IHttpError } from '@/@data'
import { EmployerStatusDropDown } from '@/components/employer'
import { useRouteQuery } from '@/hooks'
import { yupResolver } from '@hookform/resolvers/yup'
import { getInitials } from '@/utils/getInitials'

export const BalanceEditTransactionDetail = ({
  onClose,
  dataBalanceTransactionDetail,
  dataBalanceTransactionDept,
  refetch
}: {
  onClose: () => void
  dataBalanceTransactionDetail: IBalanceTransactionRecord
  dataBalanceTransactionDept: any
  refetch: () => void
}) => {
  const n = useNotification()
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const { id, transactionId } = useParams()

  const config = getStatusConfig(dataBalanceTransactionDetail.status)

  const statusOptions = useTransactionStatusOptions()

  const selectedOption = statusOptions.find(option => option.value === dataBalanceTransactionDetail.status)

  const month = searchQuery.get('month') || new Date().toISOString()

  const { mutate: updateBalanceTransaction } = useBalanceTransactionUpdateMutation()

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={80} />
    } else {
      return (
        <CustomAvatar skin='light' size={80}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const remaining: any = useMemo(() => {
    return {
      voucher: dataBalanceTransactionDept?.data?.voucher_no_tax?.remaining,
      voucher_tax: dataBalanceTransactionDept?.data?.voucher_tax?.remaining,
      debt_disbursement: dataBalanceTransactionDept?.data?.return_wages_disbursement?.remaining,
      different: dataBalanceTransactionDept?.data?.different?.remaining
    }
  }, [id, month, dataBalanceTransactionDept])

  const methods = useForm<any>({
    defaultValues: {
      voucher: 0,
      voucher_tax: 0,
      debt_disbursement: 0,
      different: 0
    },
    mode: 'onChange',
    resolver: yupResolver(getBalanceTransactionEditSchema(__, remaining))
  })

  useEffect(() => {
    methods.reset({
      voucher: dataBalanceTransactionDetail?.voucher,
      voucher_tax: dataBalanceTransactionDetail?.voucher_tax,
      debt_disbursement: dataBalanceTransactionDetail?.debt_disbursement,
      different: dataBalanceTransactionDetail?.different
    })
  }, [dataBalanceTransactionDetail])

  const onSubmit = async (data: any) => {
    const payload = {
      voucher: data?.voucher,
      voucher_tax: data?.voucher_tax,
      debt_disbursement: data?.debt_disbursement,
      different: data?.different
    }

    updateBalanceTransaction(
      {
        id: Number(transactionId),
        data: payload
      },
      {
        onSuccess: () => {
          n.success(__('Attendance reconcile updated successfully'))
          onClose()
          refetch()
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  const values = [
    methods.watch('voucher') || 0,
    methods.watch('voucher_tax') || 0,
    methods.watch('debt_disbursement') || 0,
    methods.watch('different') || 0
  ]

  const receiveDifferent = Number(methods.watch('different') || 0)

  const total = values.reduce((sum, val) => sum + Number(val), 0)

  const employerPercentage = Number(
    (
      (receiveDifferent /
        Number(
          dataBalanceTransactionDept?.data?.different?.reconciled
            ? dataBalanceTransactionDept?.data?.different?.reconciled
            : 0
        )) *
      100
    ).toFixed(2)
  )

  const liftPercentage = Number((100 - employerPercentage).toFixed(2))

  const wageDisbursementDifference = Number(dataBalanceTransactionDept?.data?.different?.reconciled)

  return (
    <BoxPage
      showBackButton={true}
      title={__('Edit Balance Transaction Detail')}
      breadcrumbs={[
        { label: __('Finance Management'), href: '/cms/finance' },
        { label: __('Balance Management'), href: '/cms/finance/balance' },
        { label: __('Balance Company Detail'), href: `/cms/finance/balance/${id}` },
        { label: __('Balance Transaction Detail'), href: `/cms/finance/balance/${id}/transaction/${transactionId}` },
        { label: __('Balance Transaction Detail Edit') }
      ]}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, sm: 9 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  mb: 6
                }}
              >
                <CardContent className='p-0'>
                  <div className='flex justify-between items-center p-6'>
                    <div className='flex flex-row gap-2 items-center'>
                      <Typography className='text-[24px] leading-[38px] font-medium'>
                        {__('TransactionID')} {dataBalanceTransactionDetail?.id}
                      </Typography>
                      <Button
                        variant='contained'
                        className={`bg-[${config.backgroundColor}] text-[${config.textColor}] shadow-none`}
                        sx={{ borderRadius: 10, height: '30px', fontSize: '13px' }}
                      >
                        {selectedOption?.label}
                      </Button>
                    </div>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <IconButton size='large' className='bg-[#EFEEF0] w-[38px] h-[38px]' onClick={onClose}>
                        <i className='tabler-x w-[16px] h-[16px]' />
                      </IconButton>

                      <IconButton size='large' className='bg-[#EFEEF0] w-[38px] h-[38px]'>
                        <i className='tabler-viewport-wide w-[16px] h-[16px]' />
                      </IconButton>
                    </Box>
                  </div>
                  <Divider />
                  <Box className='p-6'>
                    <Grid container spacing={6}>
                      <Grid size={{ xs: 12, md: 8.5 }}>
                        <Box className='flex flex-row gap-2 items-center w-full bg-[#D4F4E2] rounded-[24px] p-4 h-[128px]'>
                          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                            {getAvatar({
                              logo_file: dataBalanceTransactionDetail?.company?.logo_file,
                              name: dataBalanceTransactionDetail?.company?.name || '-'
                            })}
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Typography
                                  variant='h5'
                                  gutterBottom
                                  sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}
                                  className='truncate max-w-[250px]'
                                  title={dataBalanceTransactionDetail?.company.name}
                                >
                                  {dataBalanceTransactionDetail?.company.name}
                                </Typography>
                                <EmployerStatusDropDown
                                  status={dataBalanceTransactionDetail?.company?.status as CompanyStatusEnum}
                                  isDetail={true}
                                  sx={{
                                    height: '30px',
                                    fontSize: '13px'
                                  }}
                                />
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <i className='tabler-id' />
                                <span className='text-[15px] leading-[22px]'>
                                  {dataBalanceTransactionDetail?.company.system_code}
                                </span>

                                <i
                                  className='tabler-copy'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      dataBalanceTransactionDetail?.company.system_code || ''
                                    )
                                    n.success(__('Copied to clipboard!'))
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3.5 }}>
                        <Box className='bg-[#3D5AFE] rounded-[24px] p-6 h-[128px]  relative overflow-hidden flex  flex-col gap-2 '>
                          <Typography className='text-[20px] leading-[32px] font-medium text-white'>
                            {dataBalanceTransactionDetail?.company?.bank?.shortName}
                          </Typography>
                          <Typography className='text-[15px] leading-[22px] font-normal text-white'>
                            {dataBalanceTransactionDetail?.company.bank_account_number}
                          </Typography>
                          <Box className='absolute -bottom-20 -right-20 w-40 h-40 bg-[#6E83FE] rounded-full opacity-80'></Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box className='flex flex-col gap-8 p-6'>
                    <div className='flex flex-col gap-2'>
                      <Typography className='text-[24px] font-medium leading-[38px]'>
                        {__('Transaction Detail')}
                      </Typography>
                      <Typography className='text-[15px] leading-[22px] font-normal '>
                        {__('Review your transaction details – Confirm to complete payment securely.')}
                      </Typography>
                    </div>
                    <div className='overflow-x-auto border rounded-[20px]'>
                      <table className='w-full border-collapse'>
                        <thead>
                          <tr className='border-b'>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              #
                            </th>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              {__('TYPE')}
                            </th>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              {__('EWA DISBURSEMENT')}
                            </th>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              {__('RECONCILED')}
                            </th>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              {__('REMAINING')}
                            </th>
                            <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                              {__('RECEIVE')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className='border-b border-gray-100 '>
                            <td className='p-4 text-[15px] font-normal leading-[22px] w-[50px]'>1</td>
                            <td className='p-4 text-[15px] font-normal leading-[22px] '>{__('Voucher')}</td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_no_tax?.ewa_disbursement, 'đ')}
                            </td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_no_tax?.reconciled, 'đ')}
                            </td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_no_tax?.remaining, 'đ')}
                            </td>
                            <td className='p-4'>
                              <FormInput
                                name='voucher'
                                className='rounded-full w-full'
                                control={methods.control}
                                type='number'
                                placeholder={__('Enter Value')}
                              />
                            </td>
                          </tr>
                          <tr className='border-b border-gray-100 '>
                            <td className='p-4 text-[15px] font-normal leading-[22px] w-[50px]'>2</td>
                            <td className='p-4 text-[15px] font-normal leading-[22px] '>{__('Voucher Tax')}</td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_tax?.ewa_disbursement, 'đ')}
                            </td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_tax?.reconciled, 'đ')}
                            </td>
                            <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.voucher_tax?.remaining, 'đ')}
                            </td>
                            <td className='p-4'>
                              <FormInput
                                name='voucher_tax'
                                className='rounded-full w-full'
                                control={methods.control}
                                type='number'
                                placeholder={__('Enter Value')}
                              />
                            </td>
                          </tr>
                          <tr className=' border-gray-100'>
                            <td className='px-4 py-4 text-[15px] font-normal leading-[22px] w-[50px]'>3</td>
                            <td className='px-4 py-4 text-[15px] font-normal leading-[22px] w-[180px]'>
                              {__('Return Wages Disbursement')}
                            </td>
                            <td className='px-4 py-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.ewa_disbursement, 'đ')}
                            </td>
                            <td className='px-4 py-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.reconciled, 'đ')}
                            </td>
                            <td className='px-4 py-4 text-left text-[15px] font-normal leading-[22px]'>
                              {__n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.remaining, 'đ')}
                            </td>
                            <td className='px-4 py-4'>
                              <FormInput
                                name='debt_disbursement'
                                className='rounded-full w-full'
                                control={methods.control}
                                type='number'
                                placeholder={__('Enter Value')}
                              />
                            </td>
                          </tr>
                          {wageDisbursementDifference > 0 && (
                            <tr className=' border-gray-100'>
                              <td className='px-4 py-4 text-[15px] font-normal leading-[22px] w-[50px]'>4</td>
                              <td className='px-4 py-4 text-[15px] font-normal leading-[22px] '>{__('Different')}</td>
                              <td className='p-4 text-[15px] font-normal leading-[22px] flex gap-4 flex-col items-start'>
                                <Chip
                                  label={__(`Employer ${employerPercentage}% `)}
                                  className={`bg-[#D6F4F7] rounded-full ${employerPercentage > 0 ? 'flex' : 'hidden'}  text-[#00BAD1] text-[13px] font-medium leading-[18px]`}
                                />
                                <Chip
                                  label={__(`Lift ${liftPercentage}% `)}
                                  className={`bg-[#DEE3FD] rounded-full ${liftPercentage > 0 ? 'flex' : 'hidden'} text-[#3551F2] text-[13px] font-medium leading-[18px] `}
                                />
                              </td>
                              <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.different?.reconciled, 'đ')}
                              </td>
                              <td className='px-4 py-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.different?.remaining, 'đ')}
                              </td>
                              <td className='px-4 py-4'>
                                <FormInput
                                  name='different'
                                  className='rounded-full w-full'
                                  control={methods.control}
                                  placeholder={__('Enter Value')}
                                  disabled={!dataBalanceTransactionDept?.data?.different?.reconciled}
                                  type='number'
                                />
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className='bg-gray-100 border-t-2 border-gray-200'>
                            <td className='px-4 py-4 text-[15px] font-medium leading-[22px] text-gray-700' colSpan={2}>
                              {__(`Total: 1 + 2 + 3 ${wageDisbursementDifference > 0 ? ' + 4' : ''}`)}
                            </td>
                            <td className='px-4 py-4'></td>
                            <td className='px-4 py-4'></td>
                            <td className='px-4 py-4'></td>
                            <td className='px-4 py-4'>
                              <Typography className='text-[15px] font-medium leading-[22px]'>
                                {__n(total, 'đ')}
                              </Typography>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card sx={{ p: 6, backgroundColor: '#F9F9F9' }} className='flex flex-col gap-4 rounded-[32px]'>
                <Button
                  variant='contained'
                  className='text-[#6F83F6] bg-[#E8EBFD] shadow-none'
                  fullWidth
                  onClick={() => methods.reset()}
                >
                  {__('Reset')}
                </Button>
                <Button variant='contained' className='text-[#28C76F] bg-[#DCF6E8] shadow-none' fullWidth type='submit'>
                  {__('Save')}
                </Button>
              </Card>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </BoxPage>
  )
}
